import * as CL from '@design-system/component-library';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { JsonLd } from 'react-schemaorg';
import { paths } from '../../common/constants/pathVariables';
import { orderMsg, t } from '../../common/i18n';
import { AnalyticsEventOrigins, AnalyticsListNames } from '../../common/utils/analyticsUtils';
import { isFiveGPlusNettiliteOffer } from '../../common/utils/subscriptionUtils';
import { SiteContext } from '../../public/site/SiteContext';
import { Grid } from '../Grid/Grid';
import { SubscriptionCard } from './SubscriptionCard';
import { MobileCarouselWrapper, getItemList, hasDiscount } from './SubscriptionUtils';
import type { InfoItem } from './SubscriptionCard';
import type { OrderProps, SubscriptionCardWithModel } from './types';
import type { NettiLiteOffersProps } from '../NettiLiteOffers/NettiLiteOffers';
import type { SubscriptionCardOfferSpecification, SubscriptionCardProps } from '../SubscriptionCard/SubscriptionCard';
import type { ItemList } from 'schema-dts';

export interface HorizontalFixedBroadbandSubscriptionsProps extends NettiLiteOffersProps {
  subscriptions: SubscriptionCardProps[];
  isMobileProduct: boolean;
}

export const HorizontalFixedBroadbandSubscriptions = ({
  subscriptions,
  fixedBroadBandOnlineModel,
  mobileBroadBandOnlineModel,
  postalCode,
  availabilityAddress,
  isMobileProduct,
}: HorizontalFixedBroadbandSubscriptionsProps) => {
  const { search, pathname } = useLocation();
  const { siteBaseUrl } = useContext(SiteContext);
  const navigate = useNavigate();

  const onOrder = ({ bbOfferCode }: OrderProps) => {
    const onlineModel = !isMobileProduct ? fixedBroadBandOnlineModel : mobileBroadBandOnlineModel;
    const selectedOffer = onlineModel.offers.find(offer => offer.offerCode === bbOfferCode);
    if (!selectedOffer) {
      return;
    }
    navigate(`${paths.ANONYMOUS_FIXED_BROADBAND_ORDER_CHECKOUT}?${new URLSearchParams(search)}`, {
      state: {
        selectedOfferCode: bbOfferCode,
        isMobileProduct,
        disableHeader: true, // The header and breadcrumbs should be hidden during checkout step
        searchAddress: availabilityAddress,
        postalCode,
        previousUrl: location.pathname,
        fixedBroadbandOnlineModel: fixedBroadBandOnlineModel,
        mobileBroadbandOnlineModel: mobileBroadBandOnlineModel,
      },
    });
  };

  const addBadges = (card: SubscriptionCardProps) => {
    const badges = [];
    if (isFiveGPlusNettiliteOffer(card.selectedOffer)) {
      badges.push(<CL.Badge color="5g" text="5G+" type="text" />);
    }
    if (hasDiscount(card as SubscriptionCardWithModel)) {
      badges.push(<CL.Badge key="offer" color="discount" text={t.TJPQ('Offer')} type="text" />);
    }
    return badges;
  };

  const extractTextFromSpecification = (specification: SubscriptionCardOfferSpecification): string => {
    if (!specification.value) {
      return specification.name;
    }
    return `${specification.name} ${specification.value}`;
  };

  const mapSpecifications = (specifications?: SubscriptionCardOfferSpecification[]): InfoItem[] =>
    specifications
      ? specifications?.map((specification: SubscriptionCardOfferSpecification) => ({
          text: extractTextFromSpecification(specification),
          icon: specification.icon || '',
        }))
      : [];

  return (
    <Grid>
      <div className="of-subscriptions-main-container">
        <MobileCarouselWrapper>
          {subscriptions.map(card => (
            <SubscriptionCard
              subscriptionCard={card as SubscriptionCardWithModel}
              key={card.selectedOffer}
              badges={card.selectedOffer ? addBadges(card) : []}
              subHeader="Yritysliittymä"
              header={`${card.name} ${card.mainHeader}`}
              description={card.description || ''}
              infoItems={mapSpecifications(card.specifications)}
              buttonMakesOrder={true}
              hasQuantity={false}
              buttonText={t.AQOL(orderMsg)}
              onOrder={onOrder}
              analyticsIndex={0}
              isChangeOffer={false}
              showFixedTermInfo={true}
              analyticsListName={`${AnalyticsEventOrigins.PUBLIC} ${AnalyticsListNames.NETTI_LITE_LIST_NAME}`}
            />
          ))}
        </MobileCarouselWrapper>
        <JsonLd<ItemList> item={getItemList(subscriptions as SubscriptionCardWithModel[], siteBaseUrl, pathname)} />
      </div>
    </Grid>
  );
};
